import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Sparkles,
  Box,
  Ruler,
  Clock,
  Scale,
  CheckCircle,
  AlertTriangle,
  Info,
  Loader2,
  Save,
  Printer,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import FileUpload from '@/components/upload/FileUpload'
import ModelViewer, { type ModelInfo } from '@/components/viewer/ModelViewer'
import { 
  analyzeModelForPrinting, 
  formatPrintTime,
  getDifficultyColor,
  getScoreColor,
  type PrintabilityAnalysis,
} from '@/lib/modelAnalysis'
import { useProjects } from '@/hooks/useProjects'
import { useDesigns } from '@/hooks/useDesigns'
import { useStorage } from '@/hooks/useStorage'
import { formatFileSize } from '@/lib/utils'

interface DesignUploadPageProps {
  onBack: () => void
  projectId?: string
}

export default function DesignUploadPage({ onBack, projectId }: DesignUploadPageProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null)
  const [analysis, setAnalysis] = useState<PrintabilityAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [designName, setDesignName] = useState('')
  const [description, setDescription] = useState('')
  
  const { projects } = useProjects()
  const { createDesign } = useDesigns()
  const { uploadDesignFile } = useStorage()

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file)
    setDesignName(file.name.replace(/\.[^/.]+$/, ''))
    setModelInfo(null)
    setAnalysis(null)
  }, [])

  const handleModelLoad = useCallback((info: ModelInfo) => {
    setModelInfo(info)
    setIsAnalyzing(true)
    
    // Simulate AI analysis delay for UX
    setTimeout(() => {
      const printAnalysis = analyzeModelForPrinting(info)
      setAnalysis(printAnalysis)
      setIsAnalyzing(false)
    }, 1500)
  }, [])

  const handleSave = async () => {
    if (!selectedFile || !modelInfo || !projectId) return
    
    setIsSaving(true)
    try {
      // Upload file to storage
      const uploadResult = await uploadDesignFile(selectedFile, projectId)
      if (!uploadResult) throw new Error('Upload failed')

      // Create design record
      await createDesign({
        project_id: projectId,
        name: designName || selectedFile.name,
        description: description || null,
        status: 'ready',
        file_path: uploadResult.path,
        file_size_bytes: selectedFile.size,
        file_type: selectedFile.name.split('.').pop()?.toLowerCase() as any,
        dimensions: modelInfo.boundingBox,
        polygon_count: modelInfo.triangles,
        is_manifold: modelInfo.isManifold,
        material_volume_cm3: modelInfo.volume / 1000,
      })

      onBack()
    } catch (error) {
      console.error('Failed to save design:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-surface-800 text-surface-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Upload Design</h1>
            <p className="text-surface-400 text-sm">Import a 3D model and let AI analyze it</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Viewer */}
          <div className="space-y-4">
            {!selectedFile ? (
              <FileUpload
                onFileSelect={handleFileSelect}
                className="min-h-[400px]"
              />
            ) : (
              <>
                <ModelViewer
                  file={selectedFile}
                  onModelLoad={handleModelLoad}
                  className="h-[400px] lg:h-[500px]"
                  autoRotate={!modelInfo}
                />
                
                {/* File Info */}
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <input
                        type="text"
                        value={designName}
                        onChange={(e) => setDesignName(e.target.value)}
                        className="text-lg font-medium text-white bg-transparent border-none outline-none focus:ring-0 w-full"
                        placeholder="Design name..."
                      />
                      <p className="text-sm text-surface-400 mt-1">
                        {formatFileSize(selectedFile.size)} • {selectedFile.name.split('.').pop()?.toUpperCase()}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedFile(null)}>
                      Change
                    </Button>
                  </div>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a description..."
                    className="w-full mt-3 px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white text-sm placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    rows={2}
                  />
                </Card>
              </>
            )}
          </div>

          {/* Right Column - Analysis */}
          <div className="space-y-4">
            <AnimatePresence mode="wait">
              {!selectedFile ? (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Card className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-800 flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-surface-500" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">AI Analysis</h3>
                    <p className="text-surface-400 text-sm">
                      Upload a 3D model to get instant printability analysis, dimension checking, and smart recommendations.
                    </p>
                  </Card>
                </motion.div>
              ) : isAnalyzing || !modelInfo ? (
                <motion.div
                  key="analyzing"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Card className="p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">Analyzing Model...</h3>
                    <p className="text-surface-400 text-sm">
                      AI is inspecting geometry, checking printability, and calculating estimates.
                    </p>
                  </Card>
                </motion.div>
              ) : analysis ? (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Printability Score */}
                  <Card className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-white">Printability Score</h3>
                      <span className={`text-2xl font-bold ${getScoreColor(analysis.score)}`}>
                        {analysis.score}%
                      </span>
                    </div>
                    <div className="h-3 bg-surface-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${analysis.score}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className={`h-full ${
                          analysis.score >= 80 ? 'bg-success-500' :
                          analysis.score >= 60 ? 'bg-warning-500' :
                          analysis.score >= 40 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <span className="text-surface-400">Difficulty</span>
                      <span className={`font-medium capitalize ${getDifficultyColor(analysis.difficulty)}`}>
                        {analysis.difficulty}
                      </span>
                    </div>
                  </Card>

                  {/* Model Stats */}
                  <Card className="p-5">
                    <h3 className="font-medium text-white mb-4">Model Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                          <Ruler className="w-5 h-5 text-primary-400" />
                        </div>
                        <div>
                          <p className="text-xs text-surface-400">Dimensions</p>
                          <p className="text-sm text-white font-medium">
                            {modelInfo.boundingBox.width} × {modelInfo.boundingBox.height} × {modelInfo.boundingBox.depth}mm
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-ai-500/20 flex items-center justify-center">
                          <Box className="w-5 h-5 text-ai-400" />
                        </div>
                        <div>
                          <p className="text-xs text-surface-400">Triangles</p>
                          <p className="text-sm text-white font-medium">
                            {modelInfo.triangles.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-success-500/20 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-success-400" />
                        </div>
                        <div>
                          <p className="text-xs text-surface-400">Est. Print Time</p>
                          <p className="text-sm text-white font-medium">
                            {formatPrintTime(analysis.estimatedPrintTime)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-warning-500/20 flex items-center justify-center">
                          <Scale className="w-5 h-5 text-warning-400" />
                        </div>
                        <div>
                          <p className="text-xs text-surface-400">Est. Material</p>
                          <p className="text-sm text-white font-medium">
                            ~{analysis.estimatedMaterial}g PLA
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Issues & Recommendations */}
                  {analysis.issues.length > 0 && (
                    <Card className="p-5">
                      <h3 className="font-medium text-white mb-3">Issues Found</h3>
                      <div className="space-y-2">
                        {analysis.issues.map((issue, i) => (
                          <div
                            key={i}
                            className={`p-3 rounded-lg flex items-start gap-3 ${
                              issue.type === 'critical' ? 'bg-red-500/10 border border-red-500/30' :
                              issue.type === 'warning' ? 'bg-warning-500/10 border border-warning-500/30' :
                              'bg-surface-800 border border-surface-700'
                            }`}
                          >
                            {issue.type === 'critical' ? (
                              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            ) : issue.type === 'warning' ? (
                              <AlertTriangle className="w-5 h-5 text-warning-400 flex-shrink-0 mt-0.5" />
                            ) : (
                              <Info className="w-5 h-5 text-surface-400 flex-shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-white">{issue.title}</p>
                              <p className="text-xs text-surface-400 mt-0.5">{issue.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* AI Recommendations */}
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-ai-400" />
                      <h3 className="font-medium text-white">AI Recommendations</h3>
                    </div>
                    <ul className="space-y-2">
                      {analysis.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-success-400 flex-shrink-0 mt-0.5" />
                          <span className="text-surface-300">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button variant="secondary" className="flex-1">
                      <Printer className="w-4 h-4" />
                      Print Now
                    </Button>
                    <Button 
                      className="flex-1" 
                      onClick={handleSave}
                      disabled={isSaving || !projectId}
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {projectId ? 'Save to Project' : 'Select Project First'}
                    </Button>
                  </div>

                  {!projectId && projects.length > 0 && (
                    <p className="text-xs text-surface-500 text-center">
                      Go to Projects to select where to save this design
                    </p>
                  )}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
